import { inject, Injectable } from '@angular/core'
import { combineLatest, map, Observable, of, switchMap } from 'rxjs'
import { HttpClient, HttpContext } from '@angular/common/http'
import { APIEnvelope } from '../shared/models/api'
import { groupBy, orderBy, uniq } from 'lodash'
import { GLOBAL_LOADING_SPINNER_ENABLED } from '../shared/interceptors/loading-spinner-interceptor'
import {
  AuditProperties,
  ChildEntityCandidate,
  EditableRow,
  Entity,
  EntityDetails,
  EntityDetailsProperties,
  EntityRelationship,
  EntityRelationshipType,
  EntityType,
  EntityTypeCode,
  entityTypeCodes,
  EntityTypeMap,
  EntityTypeMetadata,
  EntityTypeProperties,
  ParentRelationshipArgs,
  RelationshipType,
} from './entity-metadata'
import { addDays } from 'date-fns'

const defaultEntityTypeProperties: EntityTypeProperties = {
  required: ['effective_date'],
  optional: ['termination_date'],
}

const withDefaults = (metadata: EntityTypeMetadata): EntityTypeMetadata => {
  if (metadata.editable) {
    return {
      ...metadata,
      required: uniq([...metadata.required, ...defaultEntityTypeProperties.required]),
      optional: uniq([...metadata.optional, ...defaultEntityTypeProperties.optional]),
    }
  } else {
    return metadata
  }
}

type DetailPropertiesMap = Readonly<Partial<Record<EntityTypeCode, EntityTypeProperties>>>

type EntityTypeRecord = Record<EntityTypeCode, EntityTypeMetadata>

type EntityResult = Entity | EntityDetails | EntityRelationship

const transformTerminationDate = <T extends string | null> (terminationDate: T, delta : number): T => {
  if (terminationDate && terminationDate < '9') {
    return addDays(terminationDate, delta).toJSON() as T
  } else {
    return terminationDate
  }
}

// python API sends dates without any time zone indicator on them, although they are UTC
// sigh
const fixDateValue = <T extends string | null | undefined> (dateString: T): T => {
  if (dateString == null || dateString.endsWith('Z')) {
    return dateString
  } else if (dateString.length === 19) {
    // no millis or nanos so just add .000Z to match Date::toJSON
    return `${dateString}.000Z` as T
  } else {
    // timestamps have nanos but no Z
    return `${dateString}Z` as T
  }
}

const fixExtended = <T extends EntityResult>(result: T): Partial<T> => {
  const { create_time, last_update_time } = result as AuditProperties
  if (create_time && last_update_time) {
    return {
      create_time: fixDateValue(create_time),
      last_update_time: fixDateValue(last_update_time),
    } as T // can't directly assert Partial<T>... seems like a TypeScript bug
  } else {
    return {}
  }
}

const fixDateValues = <T extends EntityResult>(result: T): T => ({
  ...result,
  effective_date: fixDateValue(result.effective_date),
  termination_date: transformTerminationDate(fixDateValue(result.termination_date), -1),
  ...fixExtended(result),
})

type BodyWithTerminationDate = Readonly<{ termination_date: string }>

const transformTerminationDateIn = <T extends BodyWithTerminationDate> (body: T, delta: number): T => ({
  ...body,
  termination_date: transformTerminationDate(body.termination_date, delta),
})

// tabbed interface really needs caching
// tried out node-cache but TS compiler didn't like it, and it looked like it hadn't been
// updated recently... decided to do a home-grown easy implementation
// if we start caching in several places we should revisit that decision
const cacheTtl = 30 * 60 // 30 minutes

type CacheItem<T> = Readonly<{
  value: T
  expiration: number
}>

type CachedOperation = 'getEntities' | 'getEntityDetails' | 'getRelationships' | 'getChildEntityCandidates'

const getCacheKey = (op: CachedOperation, ...args: readonly string[]): string =>
  [op, ...args].join('\t')

const entitiesCacheKey = (entityTypeCode: EntityTypeCode): string => getCacheKey('getEntities', entityTypeCode)

const entityDetailsCacheKey = (entityUID: string) =>
  getCacheKey('getEntityDetails', entityUID)

const relationshipsCacheKey = (entityUID: string, relatedTypeCode: EntityTypeCode, relationshipType: RelationshipType): string =>
  getCacheKey('getRelationships', entityUID, relatedTypeCode, relationshipType)

const childEntityCandidatesCacheKey = (entityUID: string, relatedEntityTypeCode: EntityTypeCode): string =>
  getCacheKey('getChildEntityCandidates', entityUID, relatedEntityTypeCode)

// workaround = not in the ess.entity_type table as of 2026-04-30 but probably should be
const additionalEntityTypes: readonly EntityType[] = [
  { entity_type_code: 'ARRZ', entity_type_name: 'ARR Zone' },
]

@Injectable({ providedIn: 'root' })
export class EntityManagementService {
  private readonly http = inject(HttpClient)

  private readonly cache: Record<string, CacheItem<unknown>> = {}

  private removeCacheItems(...keys: readonly string[]) {
    keys.forEach(key => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.cache[key]
    })
  }

  private lookup(key: string): CacheItem<unknown> | undefined {
    const item = this.cache[key]
    if (item) {
      if (item.expiration > Date.now()) {
        return item
      } else {
        this.removeCacheItems(key)
      }
    }
    return undefined
  }

  private getWithCache<T>(key: string, fetch: () => Observable<T>): Observable<T> {
    const item = this.lookup(key)
    if (item) {
      return of(item.value as T)
    } else {
      return fetch().pipe(map(result => this.putCacheItem(key, result)))
    }
  }


  private putCacheItem<T>(key: string, value: T): T {
    this.cache[key] = {
      expiration: Date.now() + 1000 * cacheTtl,
      value,
    }
    return value
  }

  getEntityTypeMap(): Observable<EntityTypeMap> {
    const isEditable = ({ required, optional }: EntityTypeProperties) =>
      required.length > 0 || optional.length > 0

    return combineLatest([
      this.getEntityTypes(),
      this.http.get<{ entities: DetailPropertiesMap }>('entities/entity_detail_properties'),
    ]).pipe(
      map(([entityTypes, { entities }]): EntityTypeMap => {
        const result: Partial<EntityTypeRecord> = {}
        entityTypes.forEach(({ entity_type_code, entity_type_name }) => {
          const serverProperties: EntityTypeProperties = entities[entity_type_code] ?? { required: [], optional: [] }
          result[entity_type_code] = withDefaults({
            entity_type_code,
            entity_type_name,
            editable: isEditable(serverProperties),
            ...serverProperties,
          })
        })
        return result as EntityTypeMap
      }),
    )
  }

  private getEntityTypes(): Observable<readonly EntityType[]> {
    return this.http.get<readonly EntityType[]>(
      `entities/entity_types?${(new URLSearchParams({ 'type_codes_csv': entityTypeCodes.join(',') }))}`,
    ).pipe(
      // see note accompanying additionalEntityTypes declaration
      map(types => {
        const isIncludedInResponse = (typeCode: EntityTypeCode) =>
          types.some(_ => _.entity_type_code === typeCode)

        const toAdd = additionalEntityTypes.filter(_ =>
          !isIncludedInResponse(_.entity_type_code),
        )
        return [...types, ...toAdd]
      }),
    )
  }

  getEntities(entityTypeCode: EntityTypeCode): Observable<readonly Entity[]> {
    return this.getWithCache(entitiesCacheKey(entityTypeCode), () =>
      this.http.get<APIEnvelope<Entity>>(`entities/${encodeURIComponent(entityTypeCode)}`).pipe(
        map(response => response.results.map(fixDateValues)),
      ),
    )
  }

  getEntityDetails(entityTypeCode: EntityTypeCode, entityUID: string): Observable<readonly EntityDetails[]> {
    return this.getWithCache(entityDetailsCacheKey(entityUID), () =>
      this.http.get<APIEnvelope<EntityDetails>>(
        `entities/${encodeURIComponent(entityTypeCode)}/${encodeURIComponent(entityUID)}/details`,
        { context: new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, false) },
      ).pipe(
        map(({ results }) =>
          orderBy(results.map(fixDateValues), 'termination_date', 'desc'),
        ),
      ),
    )
  }

  updateDetailsRows(
    entityTypeCode: EntityTypeCode,
    entityUID: string,
    rows: readonly EditableRow<EntityDetails>[],
  ): Observable<unknown> {
    this.removeCacheItems(entitiesCacheKey(entityTypeCode))
    this.removeCacheItems(entityDetailsCacheKey(entityUID))
    const body = {
      rows: rows.map(row => ({
        ...row.new ? {} : { previous_effective_date: row.original!.effective_date },
        values: {
          effective_date: row.effective_date!,
          termination_date: transformTerminationDate(row.termination_date!, 1),
          min_limit: row.min_limit,
          max_limit: row.max_limit,
          miso_id: row.miso_id,
          asset_name: row.asset_name,
          mdma_weather_sensitive_flag: row.mdma_weather_sensitive_flag,
          oasis_number: row.oasis_number,
          entity_detail_value: row.entity_detail_value,
          entity_business_type: row.entity_business_type,
        },
      })),
    }
    return this.http.post(`entities/${entityTypeCode}/${entityUID}/details`, body)
  }

  createNewEntity(entityTypeCode: EntityTypeCode, entityCode: string, entityName: string, details?: EntityDetailsProperties): Observable<string> {
    this.removeCacheItems(entitiesCacheKey(entityTypeCode))
    const entityObservable = this.http.post<Entity>(
      `entities/${encodeURIComponent(entityTypeCode)}`,
      { entity_code: entityCode, entity_name: entityName },
    )

    if (details) {
      return entityObservable.pipe(
        switchMap(entity =>
          this.http.post(`entities/${entityTypeCode}/${entity.entity_uid}/details`, { rows: [{ values: transformTerminationDateIn(details, 1) }] })
            .pipe(map(() => entity.entity_uid)),
        ),
      )
    } else {
      return entityObservable.pipe(map(_ => _.entity_uid))
    }
  }

  getRelationships(
    entity: Entity,
    relatedEntityType: EntityTypeCode,
    relationshipType: RelationshipType,
  ): Observable<EntityRelationship[]> {
    return this.getWithCache(relationshipsCacheKey(entity.entity_uid, relatedEntityType, relationshipType), () => {
      const url = `entities/${entity.entity_type_code}/${entity.entity_uid}/relationships/` +
        `${relationshipType}/${relatedEntityType}`
      const context = new HttpContext().set(GLOBAL_LOADING_SPINNER_ENABLED, false)
      return this.http.get<APIEnvelope<EntityRelationship>>(url, { context }).pipe(
        map(_ =>
          orderBy(_.results.map(fixDateValues), 'termination_date', 'desc'),
        ),
      )
    })
  }

  getRelationshipTypes(): Observable<readonly EntityRelationshipType[]> {
    // filter out relationship types involving entity types not supported by the UI
    const isValid = (relationshipType: EntityRelationshipType) =>
      entityTypeCodes.includes(relationshipType.child_entity_type_code) &&
      entityTypeCodes.includes(relationshipType.parent_entity_type_code)

    return this.http.get<readonly EntityRelationshipType[]>('entities/relationship_types').pipe(
      map(results => results.filter(_ => isValid(_))),
    )
  }

  getChildEntityCandidates(entity: Entity, childEntityTypeCode: EntityTypeCode): Observable<readonly ChildEntityCandidate[]> {
    const fixTerminationDate = (result: ChildEntityCandidate): ChildEntityCandidate => ({
      ...result,
      termination_date: transformTerminationDate(fixDateValue(result.termination_date), -1),
    })

    return this.getWithCache(childEntityCandidatesCacheKey(entity.entity_uid, childEntityTypeCode), () => {
      return this.http.get<APIEnvelope<ChildEntityCandidate>>(
        `entities/child_relationship_candidates/${entity.entity_uid}/${childEntityTypeCode}`,
      ).pipe(map(({ results }) => results.map(fixTerminationDate)))
    })
  }

  private updateParentRelationships(
    childTypeCode: EntityTypeCode,
    childEntityUid: string,
    parentTypeCode: EntityTypeCode,
    rows: readonly ParentRelationshipArgs[], // termination dates already transformed below
  ): Observable<unknown> {
    this.removeCacheItems(relationshipsCacheKey(childEntityUid, parentTypeCode, 'parent'))
    rows.forEach(({ values: { related_entity_uid } }) => {
      this.removeCacheItems(relationshipsCacheKey(related_entity_uid, childTypeCode, 'child'))
      this.removeCacheItems(childEntityCandidatesCacheKey(related_entity_uid, childTypeCode))
    })

    return this.http.post(`entities/${childTypeCode}/${childEntityUid}/relationships/parent/${parentTypeCode}`, { rows })
  }

  updateRelationships(
    entity: Entity,
    relatedEntityTypeCode: EntityTypeCode,
    relationshipType: RelationshipType,
    rows: readonly EditableRow<EntityRelationship>[],
  ): Observable<unknown> {
    const toUpdate = (row: EditableRow<EntityRelationship>): ParentRelationshipArgs => ({
      ...row.new ? {} : { entity_relationship_uid: row.entity_relationship_uid },
      values: {
        related_entity_uid: relationshipType === 'parent' ? row.parent_entity_uid! : entity.entity_uid,
        effective_date: row.effective_date!,
        termination_date: transformTerminationDate(row.termination_date!, 1),
      },
    })

    if (relationshipType === 'parent') {
      return this.updateParentRelationships(entity.entity_type_code, entity.entity_uid, relatedEntityTypeCode, rows.map(toUpdate))
    } else {
      const groups = groupBy(rows, 'child_entity_uid')
      return combineLatest(Object.keys(groups).map(childEntityUid =>
        this.updateParentRelationships(
          relatedEntityTypeCode,
          childEntityUid,
          entity.entity_type_code,
          groups[childEntityUid].map(toUpdate),
        ),
      ))
    }
  }
}